<script lang="ts">
  import { activeTab, summary, trace, detailLoading } from '../stores/execution-detail';
  import { selectedEvent } from '../stores/debugger-timeline';
  import type { DetailTab, EventStatus } from '../types/debugger';
  import { animate, stagger } from 'motion';
  import { onMount } from 'svelte';
  import TraceSummary from './TraceSummary.svelte';
  import LogViewer from './LogViewer.svelte';

  let summaryGridEl: HTMLDivElement | undefined = $state();

  $effect(() => {
    if ($summary && summaryGridEl && $activeTab === 'summary') {
      const rows = summaryGridEl.querySelectorAll('[data-summary-row]');
      if (rows.length > 0) {
        animate(Array.from(rows), { opacity: [0, 1], x: [-8, 0] }, { duration: 0.3, delay: stagger(0.04) });
      }
    }
  });

  const tabs: { id: DetailTab; label: string }[] = [
    { id: 'summary', label: 'Summary' },
    { id: 'trace', label: 'Trace' },
    { id: 'logs', label: 'Logs' },
  ];

  const statusBadge: Record<EventStatus, string> = {
    SUCCESS: 'bg-success/20 text-success',
    FAILED: 'bg-error/20 text-error',
    IN_PROGRESS: 'bg-accent/20 text-accent',
    PENDING: 'bg-text-muted/20 text-text-muted',
    SKIPPED: 'bg-warning/20 text-warning',
  };

  function formatDuration(ms?: number): string {
    if (!ms) return '—';
    return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`;
  }
</script>

<div class="flex flex-col h-full bg-bg-panel">
  {#if !$selectedEvent}
    <div class="flex flex-col items-center justify-center h-full text-center px-8 animate-fade-in-up">
      <div class="w-16 h-16 rounded-full bg-bg-card flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      </div>
      <p class="text-sm text-text-muted">Select a timeline event to view details</p>
    </div>
  {:else}
    <!-- Tab Bar -->
    <div class="flex border-b border-border shrink-0">
      {#each tabs as tab}
        <button
          onclick={() => activeTab.set(tab.id)}
          class="relative px-5 py-3 text-sm font-medium transition-colors
            {$activeTab === tab.id ? 'text-accent' : 'text-text-secondary hover:text-text-primary'}"
        >
          {tab.label}
          {#if $activeTab === tab.id}
            <div class="absolute bottom-0 left-0 right-0 h-0.5 bg-accent rounded-full"></div>
          {/if}
        </button>
      {/each}
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto">
      {#if $detailLoading}
        <div class="p-4 space-y-3">
          {#each Array(6) as _, i (i)}
            <div class="flex gap-3">
              <div class="h-4 w-24 bg-bg-card rounded animate-skeleton-pulse"></div>
              <div class="h-4 flex-1 bg-bg-card rounded animate-skeleton-pulse"></div>
            </div>
          {/each}
        </div>
      {:else if $activeTab === 'summary' && $summary}
        <div class="p-4 animate-fade-in-up">
          <div bind:this={summaryGridEl} class="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm">
            <span data-summary-row class="text-text-muted">Execution ID</span>
            <span data-summary-row class="text-text-primary font-mono text-xs">{$summary.executionId}</span>

            <span data-summary-row class="text-text-muted">Advertiser ID</span>
            <span data-summary-row class="text-text-primary font-mono text-xs">{$summary.advertiserId}</span>

            {#if $summary.campaignId}
              <span data-summary-row class="text-text-muted">Campaign ID</span>
              <span data-summary-row class="text-text-primary font-mono text-xs">{$summary.campaignId}</span>
            {/if}

            <span data-summary-row class="text-text-muted">Type</span>
            <span data-summary-row class="text-text-primary text-xs">{$summary.type}</span>

            <span data-summary-row class="text-text-muted">Status</span>
            <span data-summary-row class="inline-flex w-fit px-2 py-0.5 rounded-full text-xs font-medium {statusBadge[$summary.status]}">
              {$summary.status}
            </span>

            <span data-summary-row class="text-text-muted">Duration</span>
            <span data-summary-row class="text-text-primary text-xs">{formatDuration($summary.duration)}</span>

            <span data-summary-row class="text-text-muted">Trigger</span>
            <span data-summary-row class="text-text-primary text-xs">{$summary.triggerSource}</span>

            {#if $summary.strategyState}
              <span data-summary-row class="text-text-muted">Strategy State</span>
              <span data-summary-row class="text-text-primary text-xs">{$summary.strategyState}</span>
            {/if}

            <span data-summary-row class="text-text-muted">Start Time</span>
            <span data-summary-row class="text-text-primary text-xs">{new Date($summary.startTime).toLocaleString()}</span>

            {#if $summary.endTime}
              <span data-summary-row class="text-text-muted">End Time</span>
              <span data-summary-row class="text-text-primary text-xs">{new Date($summary.endTime).toLocaleString()}</span>
            {/if}
          </div>

          {#if $summary.error}
            <div class="mt-4 px-3 py-2 rounded-lg bg-error/10 border border-error/20 text-error text-xs font-mono whitespace-pre-wrap">
              {$summary.error}
            </div>
          {/if}
        </div>
      {:else if $activeTab === 'trace' && $trace}
        <div class="p-4">
          <TraceSummary node={$trace} />
        </div>
      {:else if $activeTab === 'logs'}
        <LogViewer />
      {/if}
    </div>
  {/if}
</div>
